import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { todayNZ, fmtDMY, fmtDur } from "@/lib/format";
import TimelineTrack from "@/components/TimelineTrack";
import { GapReasonDialog } from "@/components/GapReasonDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, GanttChartSquare } from "lucide-react";

export default function MyTimelineDialog({ open, onClose }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const date = todayNZ();
  const [gap, setGap] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["my-timeline", date],
    queryFn: async () => (await api.get(`/my/timeline?date=${date}`)).data,
    enabled: open,
    refetchInterval: open ? 30000 : false,
  });

  const lane = data?.lanes?.[0];

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-3xl" data-testid="my-timeline-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GanttChartSquare className="h-5 w-5 text-indigo-500" /> {t("My day")}
            </DialogTitle>
            <DialogDescription>
              {fmtDMY(date)}
              {lane?.items?.length ? ` · ${t("{{dur}} worked", { dur: fmtDur(lane.worked_seconds) })}` : ""}
              {t(" — tap an amber idle gap to add a reason (travel, waiting on key, etc.).")}
            </DialogDescription>
          </DialogHeader>
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <TimelineTrack
              lanes={data?.lanes || []}
              date={date}
              showLaneLabels={false}
              onGapClick={(g) => setGap(g)}
            />
          )}
        </DialogContent>
      </Dialog>

      <GapReasonDialog
        open={!!gap}
        onClose={() => setGap(null)}
        gap={gap}
        date={date}
        onSaved={() => qc.invalidateQueries({ queryKey: ["my-timeline", date] })}
      />
    </>
  );
}
